import {
  comboboxClassNames,
  makeStyles,
  mergeClasses,
  shorthands,
  useComboboxStyles_unstable,
} from "@fluentui/react-components";

// eslint-disable-next-line no-restricted-imports
import { getTextContainerClassname } from "@1js/acui-combobox";
import { controlStylingConstants } from "@1js/acui-common";
// eslint-disable-next-line no-restricted-imports
import {
  commonHighContrastColorButtonRest,
  commonHighContrastColorDisabled,
  commonHighContrastDisabledStyles,
  commonHighContrastHoverBorderStyles,
  commonMediumContrastBackgroundHoverFocus,
  commonMediumContrastColorHoverFocus,
  HighContrastSelector,
  useHoveredIconHighContrastStyles,
} from "@1js/acui-common/lib/styling/next";
import { customTokens } from "@1js/acui-theme";
import { ribbonComboboxFastDisabledTargetClass } from "@1js/acui-font-picker";

import type {
  RibbonComboboxProps,
  RibbonComboboxState,
} from "./RibbonCombobox.types";
import {
  comboboxRootHeight,
  commonLeadingComboboxIds,
  commonTrailingComboboxIds,
} from "../RibbonComboboxConstants";

const ribbonComboboxExpandIconBackgroundColorVar =
  "--acuiRibbonCombobox__expandIcon--backgroundColor";
export const ribbonComboboxWidthToUseVar = "--acuiRibbonCombobox--widthToUse";

export const ribbonComboboxDisabledStyles = {
  backgroundColor: customTokens.colorNeutralBackgroundDisabled,
  cursor: "default",
  [`${HighContrastSelector}`]: {
    backgroundColor: "transparent",
  },
};

const useRootStyles = makeStyles({
  base: {
    borderBottomColor: customTokens.colorNeutralStroke1,
    columnGap: 0,
    height: comboboxRootHeight,
    ...shorthands.margin(
      customTokens.spacingVerticalXXS,
      customTokens.spacingHorizontalNone
    ),
    minWidth: "unset",
    paddingRight: customTokens.spacingHorizontalNone,
    width: `var(${ribbonComboboxWidthToUseVar})`,

    [`${ribbonComboboxExpandIconBackgroundColorVar}`]:
      customTokens.colorNeutralBackground1,

    ":hover": {
      borderBottomColor: customTokens.colorNeutralStroke1,

      [`${ribbonComboboxExpandIconBackgroundColorVar}`]:
        customTokens.colorNeutralBackground1Hover,
    },

    ":active": {
      borderBottomColor: customTokens.colorNeutralStroke1,
    },

    "::after": {
      borderBottomWidth: customTokens.strokeWidthThin,
    },

    ":disabled": ribbonComboboxDisabledStyles,

    [`${HighContrastSelector}`]: {
      border: `${customTokens.strokeWidthThin} solid ButtonText`,
      ":hover": {
        ...commonHighContrastHoverBorderStyles,
      },
      ":disabled": {
        ...commonHighContrastColorDisabled,
      },
    },
  },

  small: {
    height: "24px",
  },
  medium: {},
  large: { height: "24px" },

  pinnedToPreviousComboboxBase: {
    borderBottomLeftRadius: customTokens.borderRadiusNone,
    borderTopLeftRadius: customTokens.borderRadiusNone,
  },
  // Apply the negative left margin when combobox is pinned to a previous combobox/font picker.
  // Use the classname to make it higher specificity and override margins set by the parent Stack
  pinnedToPreviousComboboxXXSmallSpacing: {
    [`&.${comboboxClassNames.root}`]: {
      marginLeft: `calc(0px - ${customTokens.spacingHorizontalXXS} - ${customTokens.strokeWidthThin})`,
    },
  },
  pinnedToPreviousComboboxSmallSpacing: {
    [`&.${comboboxClassNames.root}`]: {
      marginLeft: `calc(0px - ${customTokens.spacingHorizontalS} - ${customTokens.strokeWidthThin})`,
    },
  },
  pinnedToNextCombobox: {
    borderBottomRightRadius: customTokens.borderRadiusNone,
    borderTopRightRadius: customTokens.borderRadiusNone,
  },
  withIdentifiersAbove: {
    marginTop: 0,
  },
});

const useIconStyles = makeStyles({
  base: {
    ...shorthands.padding(0),
  },
  small: {
    width: "16px",
    height: "16px",
  },
  medium: {
    width: "20px",
    height: "20px",
  },
  large: { width: "16px", height: "16px" },
  inMenu: {
    paddingRight: customTokens.spacingHorizontalMNudge,
  },
});

const useInputStyles = makeStyles({
  base: {
    boxSizing: "border-box",
    height: "100%",
    textOverflow: "ellipsis",
    width: "100%",
    "::selection": {
      backgroundColor: customTokens.legacy_neutralPrimaryAlt,
      color: customTokens.legacy_white,
    },
    [`&.${comboboxClassNames.input}`]: {
      paddingLeft: customTokens.spacingHorizontalS,
      paddingRight: 0,
    },
    [`${HighContrastSelector}`]: {
      ...commonHighContrastColorButtonRest,
      ":hover": {
        ...commonMediumContrastColorHoverFocus,
      },
    },
  },
  small: {
    fontSize: customTokens.fontSizeBase200,
  },
  medium: {},
  large: { fontSize: customTokens.fontSizeBase200 },
});

const useExpandIconStyles = makeStyles({
  base: {
    alignItems: "center",
    backgroundColor: `var(${ribbonComboboxExpandIconBackgroundColorVar})`,
    borderBottomRightRadius: customTokens.borderRadiusMedium,
    borderTopRightRadius: customTokens.borderRadiusMedium,
    display: "inline-flex",
    height: "100%",
    marginLeft: customTokens.spacingHorizontalNone,
    fill: customTokens.colorNeutralForeground1,

    ":hover": {
      [`${ribbonComboboxExpandIconBackgroundColorVar}`]:
        customTokens.colorNeutralBackground1Hover,
    },

    ":active": {
      [`${ribbonComboboxExpandIconBackgroundColorVar}`]:
        customTokens.colorNeutralBackground1Pressed,
    },

    [`${HighContrastSelector}`]: {
      ":hover": {
        ...commonMediumContrastBackgroundHoverFocus,
        ...useHoveredIconHighContrastStyles,
      },

      ":hover:active": {
        backgroundColor: "Window",
      },
    },
  },
  baseWithMinTargetSize: {
    minWidth: `${controlStylingConstants.minTargetSize}px`,
    justifyContent: "center",
  },
  expanded: {
    [`${ribbonComboboxExpandIconBackgroundColorVar}`]:
      customTokens.colorNeutralBackground1Selected,
  },
  pinnedToNextCombobox: {
    borderBottomRightRadius: customTokens.borderRadiusNone,
    borderTopRightRadius: customTokens.borderRadiusNone,
  },
});

const useComboboxLabelStyles = makeStyles({
  base: {
    boxSizing: "border-box",
    lineHeight: "normal",
    overflowWrap: "break-word",
    ...shorthands.padding("5px", 0),
    pointerEvents: "none",
  },
  small: {
    ...shorthands.margin("6px", "4px", "7px", "4px"),
  },
  medium: {
    ...shorthands.margin("6px", "6px", "7px", "2px"),
  },
  large: { ...shorthands.margin("6px", "4px", "7px", "4px") },
});

const useIconLabelWrapperStyles = makeStyles({
  base: {
    alignItems: "center",
    display: "flex",
    marginLeft: "6px",
  },
  baseWithIdentifiersAbove: {
    marginLeft: 0,
  },
});

const useWrapperStyles = makeStyles({
  base: {
    alignItems: "center",
    display: "flex",
  },
  small: {
    height: "24px",
    marginRight: customTokens.spacingHorizontalXXS,
  },
  medium: {
    height: "32px",
    marginRight: customTokens.spacingHorizontalS,
  },
  large: { height: "24px", marginRight: customTokens.spacingHorizontalXXS },
  isInMenuNoIcon: {
    marginLeft: `${controlStylingConstants.menuNoIconGutterPadding}px`,
  },
  isInMenuWithIcon: {
    marginLeft: `${controlStylingConstants.menuItemGapToRightEdge}px`,
    marginRight: `${controlStylingConstants.menuItemGapToRightEdge}px`,
  },
  withIdentifiersAbove: {
    alignItems: "flex-start",
    flexDirection: "column",
    height: "auto",
  },
});

const useDisabledStyles = makeStyles({
  base: {
    [`${HighContrastSelector}`]: {
      ...commonHighContrastDisabledStyles,
    },
  },
  control: {
    color: customTokens.colorNeutralForegroundDisabled,
    backgroundColor: customTokens.colorNeutralBackgroundDisabled,
    pointerEvents: "none",
    [`${HighContrastSelector}`]: {
      ...commonHighContrastColorDisabled,
    },
  },
});

export const useRibbonComboboxCustomStyles: (
  state: RibbonComboboxState,
  props: RibbonComboboxProps
) => RibbonComboboxState = (state, props) => {
  const { pinToPreviousComboBox, size, icon } = state;
  const { id } = state.wrapper;
  const {
    disabled,
    isMenuItem,
    identifiersAbove,
    useXXSSpacingIfPinToPreviousCombobox,
  } = props;

  const rootStyles = useRootStyles();
  const inputStyles = useInputStyles();
  const expandIconStyles = useExpandIconStyles();
  const comboboxLabelStyles = useComboboxLabelStyles();
  const wrapperStyles = useWrapperStyles();
  const iconStyles = useIconStyles();
  const iconLabelWrapperStyles = useIconLabelWrapperStyles();
  const disabledStyles = useDisabledStyles();
  const useXXSmallSpacing =
    size === "large" || useXXSSpacingIfPinToPreviousCombobox;
  state.root.className = mergeClasses(
    rootStyles.base,
    rootStyles[size],
    id &&
      commonTrailingComboboxIds.includes(id) &&
      pinToPreviousComboBox &&
      mergeClasses(
        rootStyles.pinnedToPreviousComboboxBase,
        useXXSmallSpacing
          ? rootStyles.pinnedToPreviousComboboxXXSmallSpacing
          : rootStyles.pinnedToPreviousComboboxSmallSpacing
      ),
    id &&
      commonLeadingComboboxIds.includes(id) &&
      rootStyles.pinnedToNextCombobox,
    identifiersAbove && rootStyles.withIdentifiersAbove,
    state.root.className,
    ribbonComboboxFastDisabledTargetClass,
    disabled && disabledStyles.base
  );

  state.input.className = mergeClasses(
    inputStyles.base,
    inputStyles[size],
    state.input.className,
    disabled && disabledStyles.control
  );

  state.iconClassName = mergeClasses(
    iconStyles.base,
    iconStyles[size],
    isMenuItem && iconStyles.medium,
    isMenuItem && iconStyles.inMenu,
    state.iconClassName
  );

  if (state.expandIcon) {
    state.expandIcon.className = mergeClasses(
      "ms-ComboBox-CaretDown-button",
      expandIconStyles.base,
      expandIconStyles.baseWithMinTargetSize,
      state.expandIcon["aria-expanded"] && expandIconStyles.expanded,
      id &&
        commonLeadingComboboxIds.includes(id) &&
        expandIconStyles.pinnedToNextCombobox,
      state.expandIcon.className,
      ribbonComboboxFastDisabledTargetClass,
      disabled && disabledStyles.control
    );
  }

  if (state.comboboxLabel) {
    state.comboboxLabel.className = mergeClasses(
      getTextContainerClassname(),
      comboboxLabelStyles.base,
      comboboxLabelStyles[size],
      state.comboboxLabel.className
    );
  }

  if (state.iconLabelWrapper) {
    state.iconLabelWrapper.className = mergeClasses(
      iconLabelWrapperStyles.base,
      identifiersAbove && iconLabelWrapperStyles.baseWithIdentifiersAbove,
      state.iconLabelWrapper.className
    );
  }

  state.wrapper.className = mergeClasses(
    wrapperStyles.base,
    wrapperStyles[size],
    isMenuItem && !icon && wrapperStyles.isInMenuNoIcon,
    isMenuItem && icon && wrapperStyles.isInMenuWithIcon,
    identifiersAbove && wrapperStyles.withIdentifiersAbove,
    state.wrapper.className
  );

  return state;
};

export const useRibbonComboboxStyles: (
  state: RibbonComboboxState,
  props: RibbonComboboxProps
) => RibbonComboboxState = (state, props) => {
  useRibbonComboboxCustomStyles(state, props);

  useComboboxStyles_unstable(state);

  return state;
};
